import React from 'react';
import { View, Text, StyleSheet } from 'react-native';

import { ClassRotation } from '@/types/api';
import {
  RowState,
  formatRemaining,
  formatRowTime,
  parseHHMM,
  rotationKey,
  rowState,
} from '@/utils/rotations';
import { colors, font, space, radius } from '@/theme/chalkCourt';

/** Kiosk colours in Chalk & Court: green running, amber < 5 min, coral just ended, faded done. */
const ROW_COLORS: Record<RowState, { bg: string; ink: string; bar: string }> = {
  current: { bg: colors.courtSoft, ink: colors.court, bar: colors.court },
  ending: { bg: colors.warnSoft, ink: colors.warn, bar: colors.warn },
  ended: { bg: colors.accentSoft, ink: colors.accentDark, bar: colors.accent },
  past: { bg: 'transparent', ink: colors.inkFaint, bar: 'transparent' },
  future: { bg: 'transparent', ink: colors.ink, bar: 'transparent' },
};

interface Props {
  rotations: ClassRotation[];
  /** Seconds since Chicago midnight, or null when the day shown isn't today (no live state). */
  nowSec: number | null;
}

/** A class's rotations as time / apparatus / station rows, coloured by the live clock. */
const RotationRows: React.FC<Props> = ({ rotations, nowSec }) => (
  <View>
    {rotations.map((r) => {
      const state: RowState = nowSec === null ? 'future' : rowState(r, nowSec);
      const c = ROW_COLORS[state];
      const live = state === 'current' || state === 'ending';
      return (
        <View
          key={rotationKey(r)}
          style={[styles.row, { backgroundColor: c.bg, borderLeftColor: c.bar }]}
          testID={`rotation-${rotationKey(r)}`}
          accessibilityLabel={`${formatRowTime(r.start)} to ${formatRowTime(r.end)}, ${
            r.apparatus
          }, ${r.station}${live ? ', now' : ''}`}>
          <Text style={[styles.time, { color: c.ink }]}>
            {formatRowTime(r.start)}–{formatRowTime(r.end)}
          </Text>
          <View style={styles.main}>
            <Text style={[styles.apparatus, { color: c.ink }]} numberOfLines={1}>
              {r.apparatus}
            </Text>
            <Text
              style={[styles.station, state === 'past' && { color: colors.inkFaint }]}
              numberOfLines={1}>
              {r.station}
            </Text>
          </View>
          {live && nowSec !== null && (
            <Text style={[styles.left, { color: c.ink }]}>
              {formatRemaining(parseHHMM(r.end) * 60 - nowSec)}
            </Text>
          )}
        </View>
      );
    })}
  </View>
);

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
    borderLeftWidth: 4,
    borderRadius: radius.sm,
    marginBottom: 2,
  },
  time: {
    fontFamily: font.displayBold,
    fontSize: 17,
    width: 92,
  },
  main: {
    flex: 1,
  },
  apparatus: {
    fontFamily: font.bodyBold,
    fontSize: 16,
  },
  station: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkSoft,
  },
  left: {
    fontFamily: font.display,
    fontSize: 20,
    marginLeft: space.sm,
  },
});

export default RotationRows;
